import { useCallback, useRef, useState } from 'react';
import { useInterval } from 'usehooks-ts';

import { TelehealthSessionInfoData } from '../../hooks/useGetTelehealthSessionInfo';

const TICK_INTERVAL_MS = 1000;

function secondsRemaining(ms: number): number {
  return Math.ceil(Math.max(0, ms) / 1000);
}

export type UseSoftBlockAutoTransitionProps = {
  data: TelehealthSessionInfoData | undefined;
  /** Fired once when the auto-transition cutoff is reached. */
  onAutoTransition: () => void;
};

export type UseSoftBlockAutoTransitionResult = {
  secondsToAutoTransition: number;
  autoTransitionToastIsVisible: boolean;
};

/**
 * Counts down past the appointment start time, surfaces a heads-up toast, then
 * auto-transitions the client to the waiting room so the soft-block flow never
 * delays care. The timer stays disabled until the soft-block timing values are
 * present.
 *
 * Timing is anchored to a `receivedAt` timestamp compared against `Date.now()`,
 * so the countdown stays correct even if a backgrounded tab throttles timers.
 */
export function useSoftBlockAutoTransition({
  data,
  onAutoTransition,
}: UseSoftBlockAutoTransitionProps): UseSoftBlockAutoTransitionResult {
  const softBlock = data?.telehealthSessionInfo.softBlock;
  const msToAutoTransition = softBlock?.msToAutoTransition ?? null;
  const msToAutoTransitionNotification = softBlock?.msToAutoTransitionNotification ?? null;
  const hasTimingValues = msToAutoTransition !== null && msToAutoTransitionNotification !== null;

  // Anchored once, on the first render where the timing values are present.
  const receivedAtRef = useRef<number | null>(null);
  if (hasTimingValues && receivedAtRef.current === null) {
    receivedAtRef.current = Date.now();
  }

  const hasTransitionedRef = useRef(false);

  const [isTimerActive, setIsTimerActive] = useState(true);
  const [autoTransitionToastIsVisible, setAutoTransitionToastIsVisible] = useState(false);
  const [secondsToAutoTransition, setSecondsToAutoTransition] = useState(() =>
    hasTimingValues ? secondsRemaining(msToAutoTransition) : 0,
  );

  useInterval(
    useCallback(
      function checkAutoTransitionThresholds() {
        const receivedAt = receivedAtRef.current;
        if (!hasTimingValues || receivedAt === null || hasTransitionedRef.current) return;

        const elapsedMs = Date.now() - receivedAt;
        setSecondsToAutoTransition(secondsRemaining(msToAutoTransition - elapsedMs));

        if (elapsedMs >= msToAutoTransition) {
          hasTransitionedRef.current = true;
          setIsTimerActive(false);
          setAutoTransitionToastIsVisible(false);
          onAutoTransition();
          return;
        }

        if (elapsedMs >= msToAutoTransitionNotification && !autoTransitionToastIsVisible) {
          setAutoTransitionToastIsVisible(true);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        hasTimingValues,
        msToAutoTransition,
        msToAutoTransitionNotification,
        autoTransitionToastIsVisible,
        onAutoTransition,
      ],
    ),
    hasTimingValues && isTimerActive ? TICK_INTERVAL_MS : null,
  );

  return { secondsToAutoTransition, autoTransitionToastIsVisible };
}
